import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { hourSettingsApi } from '@api/client'
import type {
  HourSettings,
  KycHourSettings,
  AmlHourSettings,
  BiometricsHourSettings,
  KybHourSettings,
  RecipesRulesHourSettings,
  WorkflowsHourSettings,
  RiskHourSettings,
  FraudTxmHourSettings,
  GeneralHourSettings,
  JiraPromptSettings,
} from './types'
import { DEFAULT_HOUR_SETTINGS, DEFAULT_JIRA_PROMPTS } from './types'

interface SettingsStore {
  hourSettings: HourSettings
  jiraPrompts: JiraPromptSettings

  // Server-sync status for hourSettings. Used by the Hours tabs to render a
  // "Saving…" / "Saved · 12s ago" indicator and surface load/save errors.
  hourSettingsLoaded: boolean
  hourSettingsSaving: boolean
  hourSettingsLastSavedAt: string | null
  hourSettingsLastUpdatedBy: string | null
  hourSettingsLoadError: string | null

  // Actions
  loadHourSettings: () => Promise<void>
  updateJiraPrompt: (version: 'v1Prompt' | 'v2Prompt', value: string) => void
  updateKycHours: <K extends keyof KycHourSettings>(field: K, value: number) => void
  updateAmlHours: <K extends keyof AmlHourSettings>(field: K, value: number) => void
  updateBiometricsHours: <K extends keyof BiometricsHourSettings>(field: K, value: number) => void
  updateKybHours: <K extends keyof KybHourSettings>(field: K, value: number) => void
  updateRecipesRulesHours: <K extends keyof RecipesRulesHourSettings>(field: K, value: number) => void
  updateWorkflowsHours: <K extends keyof WorkflowsHourSettings>(field: K, value: number) => void
  updateRiskHours: <K extends keyof RiskHourSettings>(field: K, value: number) => void
  updateFraudTxmHours: <K extends keyof FraudTxmHourSettings>(field: K, value: number) => void
  updateGeneralHours: <K extends keyof GeneralHourSettings>(field: K, value: number) => void
  resetToDefaults: () => void
}

// Module-level debounce timer shared by every hour-settings update action.
// Each keystroke clears the previous timer and sets a fresh 500 ms one, so
// rapid edits batch into a single PUT. The whole hourSettings blob is
// small enough that replacing it wholesale is fine.
const SAVE_DEBOUNCE_MS = 500
let saveTimer: ReturnType<typeof setTimeout> | null = null

function mergeHourSettings(incoming: Partial<HourSettings>): HourSettings {
  // Deep-merge with defaults so any newly-added DEFAULT_HOUR_SETTINGS keys
  // are picked up by clients running against older stored payloads.
  return {
    kyc: { ...DEFAULT_HOUR_SETTINGS.kyc, ...(incoming.kyc || {}) },
    aml: { ...DEFAULT_HOUR_SETTINGS.aml, ...(incoming.aml || {}) },
    biometrics: { ...DEFAULT_HOUR_SETTINGS.biometrics, ...(incoming.biometrics || {}) },
    kyb: { ...DEFAULT_HOUR_SETTINGS.kyb, ...(incoming.kyb || {}) },
    recipesRules: { ...DEFAULT_HOUR_SETTINGS.recipesRules, ...(incoming.recipesRules || {}) },
    workflows: { ...DEFAULT_HOUR_SETTINGS.workflows, ...(incoming.workflows || {}) },
    risk: { ...DEFAULT_HOUR_SETTINGS.risk, ...(incoming.risk || {}) },
    fraudTxm: { ...DEFAULT_HOUR_SETTINGS.fraudTxm, ...(incoming.fraudTxm || {}) },
    general: { ...DEFAULT_HOUR_SETTINGS.general, ...(incoming.general || {}) },
  }
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => {
      // Schedule a debounced PUT of the current hourSettings to the server.
      // The set() argument lets us update the saving/last-saved fields from
      // inside the timer without going through the public action surface.
      function scheduleSave() {
        if (saveTimer) clearTimeout(saveTimer)
        saveTimer = setTimeout(async () => {
          saveTimer = null
          set({ hourSettingsSaving: true })
          try {
            const res = await hourSettingsApi.put(get().hourSettings)
            if (res.success && res.data) {
              set({
                hourSettingsSaving: false,
                hourSettingsLastSavedAt: res.data.updatedAt,
                hourSettingsLastUpdatedBy: res.data.updatedBy,
                hourSettingsLoadError: null,
              })
            } else {
              set({
                hourSettingsSaving: false,
                hourSettingsLoadError: res.error || 'Failed to save hour settings',
              })
            }
          } catch (e) {
            set({
              hourSettingsSaving: false,
              hourSettingsLoadError:
                e instanceof Error ? e.message : 'Failed to save hour settings',
            })
          }
        }, SAVE_DEBOUNCE_MS)
      }

      return {
        hourSettings: DEFAULT_HOUR_SETTINGS,
        jiraPrompts: DEFAULT_JIRA_PROMPTS,

        hourSettingsLoaded: false,
        hourSettingsSaving: false,
        hourSettingsLastSavedAt: null,
        hourSettingsLastUpdatedBy: null,
        hourSettingsLoadError: null,

        loadHourSettings: async () => {
          try {
            const res = await hourSettingsApi.get()
            if (res.success && res.data) {
              const incoming = res.data.hourSettings as Partial<HourSettings> | null
              if (incoming) {
                set({
                  hourSettings: mergeHourSettings(incoming),
                  hourSettingsLoaded: true,
                  hourSettingsLastSavedAt: res.data.updatedAt,
                  hourSettingsLastUpdatedBy: res.data.updatedBy,
                  hourSettingsLoadError: null,
                })
              } else {
                // No server row yet — keep DEFAULT_HOUR_SETTINGS in memory and
                // mark as loaded. The first admin edit will seed the row.
                set({ hourSettingsLoaded: true, hourSettingsLoadError: null })
              }
            } else {
              set({
                hourSettingsLoaded: true, // still allow the UI to render with defaults
                hourSettingsLoadError: res.error || 'Failed to load hour settings',
              })
            }
          } catch (e) {
            set({
              hourSettingsLoaded: true,
              hourSettingsLoadError:
                e instanceof Error ? e.message : 'Failed to load hour settings',
            })
          }
        },

        updateJiraPrompt: (version, value) =>
          set((state) => ({
            jiraPrompts: { ...state.jiraPrompts, [version]: value },
          })),

        updateKycHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              kyc: { ...state.hourSettings.kyc, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateAmlHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              aml: { ...state.hourSettings.aml, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateBiometricsHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              biometrics: { ...state.hourSettings.biometrics, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateKybHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              kyb: { ...state.hourSettings.kyb, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateRecipesRulesHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              recipesRules: { ...state.hourSettings.recipesRules, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateWorkflowsHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              workflows: { ...state.hourSettings.workflows, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateRiskHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              risk: { ...state.hourSettings.risk, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateFraudTxmHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              fraudTxm: { ...state.hourSettings.fraudTxm, [field]: value },
            },
          }))
          scheduleSave()
        },

        updateGeneralHours: (field, value) => {
          set((state) => ({
            hourSettings: {
              ...state.hourSettings,
              general: { ...state.hourSettings.general, [field]: value },
            },
          }))
          scheduleSave()
        },

        resetToDefaults: () => {
          set({
            hourSettings: DEFAULT_HOUR_SETTINGS,
            jiraPrompts: DEFAULT_JIRA_PROMPTS,
          })
          scheduleSave()
        },
      }
    },
    {
      name: 'dealhub-settings',
      // hourSettings now lives on the server — DO NOT persist it to
      // localStorage, otherwise old browsers would race the server load and
      // re-introduce per-browser drift. jiraPrompts stays browser-local for
      // now (separate migration when we want them team-wide too).
      partialize: (state) => ({ jiraPrompts: state.jiraPrompts }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<SettingsStore>
        return {
          ...currentState,
          jiraPrompts: { ...DEFAULT_JIRA_PROMPTS, ...(persisted.jiraPrompts || {}) },
        }
      },
    }
  )
)
